import * as i18n from "@solid-primitives/i18n"
import { dateFormatter } from "@/utils/date"
import { createEffect, createMemo, createResource } from "solid-js"
import { createStore } from "solid-js/store"
import { createSimpleContext } from "@opencode-ai/ui/context"
import { pluralCategory, type UiI18nPluralKey } from "@opencode-ai/ui/context/i18n"
import { Persist, persisted } from "@/utils/persist"
import { dict as en } from "@/i18n/en"
import { dict as uiEn } from "@opencode-ai/ui/i18n/en"
import {
  createDesktopNativeBundle,
  detectDesktopNativeLocale,
  DESKTOP_NATIVE_ENGLISH,
  DESKTOP_NATIVE_LABELS,
  DESKTOP_NATIVE_LOCALES,
  DESKTOP_NATIVE_LOCALE_TAGS,
  type DesktopNativeBundle,
  type DesktopNativeLocale,
} from "@/i18n/desktop-native"

export type Locale = DesktopNativeLocale
export type Direction = "ltr" | "rtl"

const RTL_LOCALES: ReadonlySet<Locale> = new Set(["ar", "ur", "pa", "fa", "dv"])

function localeDirection(locale: Locale): Direction {
  return RTL_LOCALES.has(locale) ? "rtl" : "ltr"
}

type RawDictionary = typeof en & typeof uiEn
type Dictionary = i18n.Flatten<RawDictionary>
type PluralKey =
  | UiI18nPluralKey
  | "session.question.pending"
  | "session.followupDock.summary"
  | "session.revertDock.summary"
type Source = { dict: Record<string, string> }

function cookie(locale: Locale) {
  return `oc_locale=${encodeURIComponent(locale)}; Path=/; Max-Age=31536000; SameSite=Lax`
}

const LOCALES: readonly Locale[] = DESKTOP_NATIVE_LOCALES

const INTL = DESKTOP_NATIVE_LOCALE_TAGS

const base = i18n.flatten({ ...en, ...uiEn })
const dicts = new Map<Locale, Dictionary>([["en", base]])

const coworkChatLabels: Partial<Record<keyof Dictionary, string>> = {
  "app.name.desktop": "Andmera",
  "settings.section.desktop": "Application",
  "settings.section.server": "Agent runtime",
  "settings.general.row.language.description": "Change the display language for Andmera",
  "settings.general.row.appearance.description": "Customise how Andmera looks on your device",
  "settings.general.row.colorScheme.description": "Choose whether Andmera follows the system, light, or dark theme",
  "settings.general.row.theme.description": "Customise how Andmera is themed.",
  "settings.desktop.wsl.description": "Run the agent runtime inside WSL on Windows.",
  "dialog.server.description": "Switch which Andmera runtime this app connects to.",
  "dialog.provider.opencode.note": "Curated models including Claude, GPT, Gemini and more",
  "dialog.provider.opencode.tagline": "Reliable optimized models",
  "dialog.provider.opencodeGo.tagline": "Low cost subscription for everyone",
  "dialog.model.unpaid.freeModels.title": "Free models provided by Andmera",
  "dialog.plugins.empty": "Plugins configured in the Andmera runtime config",
  "settings.updates.row.startup.description": "Automatically check for updates when Andmera launches",
  "settings.updates.toast.latest.description": "You're running the latest version of Andmera.",
  "sidebar.gettingStarted.line1": "Andmera includes configured models so you can start immediately.",
  "help.tabs.introduction": "Andmera is built around tabs.",
  "help.tabs.worktrees":
    "The new design does not support Git Worktrees yet, it's coming soon. So if you'd prefer to continue using the previous layout, you can switch between layouts in Settings. Just keep in mind that the new layout will become permanent in a few weeks.",
  "toast.update.description": "A new version of Andmera ({{version}}) is now available to install.",
  "error.page.report.prefix": "Please report this error to the Andmera team",
  "error.chain.checkConfig": "Check your config provider/model names",
  "error.chain.mcpFailed": 'MCP server "{{name}}" failed. Note, Andmera does not support MCP authentication yet.',
  "command.category.session": "Chat",
  "command.session.previous": "Previous chat",
  "command.session.next": "Next chat",
  "command.session.previous.unseen": "Previous unread chat",
  "command.session.next.unseen": "Next unread chat",
  "command.session.archive": "Archive chat",
  "command.session.new": "New chat",
  "ui.promptInput.attachments": "Upload files",
  "command.session.compact": "Compact chat",
  "command.session.compact.description": "Summarize the chat to reduce context size",
  "command.session.fork": "Fork from message",
  "command.session.fork.description": "Create a new chat from a previous message",
  "command.session.share": "Share chat",
  "command.session.share.description": "Share this chat and copy the URL to clipboard",
  "command.session.unshare": "Unshare chat",
  "command.session.unshare.description": "Stop sharing this chat",
  "command.session.export": "Export chat",
  "command.session.export.description": "Export the full chat transcript as JSON",
  "palette.search.placeholder": "Search files, commands, and chats",
  "palette.search.placeholder.home": "Search commands and chats",
  "context.export.session": "Export chat",
  "context.stats.session": "Chat",
  "context.stats.sessionCreated": "Chat Created",
  "prompt.toast.sessionCreateFailed.title": "Failed to create chat",
  "prompt.toast.promptSendFailed.description": "Unable to retrieve chat",
  "help.tabs.toast.ariaLabel": "Introducing Tabs. Organize your work and active chats with tabs",
  "help.tabs.description": "Organize your work and active chats with tabs",
  "help.tabs.sessions":
    "Start a new chat in a tab, or open an existing chat from any of your projects. Open a new tab when you're starting something new, and close it when you're done.",
  "help.tabs.persistence":
    "Keeping a few tabs open makes it easier to organize your active chats. Rename tabs to something memorable if you plan to keep them around.",
  "help.tabs.home":
    "You'll find all your chats and projects on the new Home screen. Selecting a chat opens it in a tab.",
  "toast.session.share.copyFailed.title": "Failed to copy URL to clipboard",
  "toast.session.share.success.title": "Chat shared",
  "toast.session.share.success.description": "Share URL copied to clipboard!",
  "toast.session.share.failed.title": "Failed to share chat",
  "toast.session.share.failed.description": "An error occurred while sharing the chat",
  "toast.session.unshare.success.title": "Chat unshared",
  "toast.session.unshare.success.description": "Chat unshared successfully!",
  "toast.session.unshare.failed.title": "Failed to unshare chat",
  "toast.session.unshare.failed.description": "An error occurred while unsharing the chat",
  "toast.session.export.success.title": "Chat exported",
  "toast.session.export.failed.title": "Failed to export chat",
  "toast.session.export.failed.description": "An error occurred while exporting the chat",
  "toast.session.listFailed.title": "Failed to load chats for {{project}}",
  "notification.action.goToSession": "Go to chat",
  "notification.session.error.title": "Chat error",
  "home.sessions.search.placeholder": "Search chats",
  "home.sessions.search.placeholder.scoped": "Search chats in {{scope}}",
  "home.sessions.search.sessions": "Chats",
  "home.sessions.search.noResults": "No chats found for {{query}}",
  "home.sessions.empty.description": "Create a chat to get started",
  "session.tab.session": "Chat",
  "session.tab.unknown": "Unknown Chat",
  "session.tab.review": "Workflow",
  "session.panel.reviewAndFiles": "Workflow",
  "session.error.notFound": "This chat cannot be found",
  "session.error.notFound.description": "This tab points to a chat that no longer exists on this server.",
  "session.review.empty": "No changes in this chat yet",
  "session.delete.failed.title": "Failed to delete chat",
  "session.delete.title": "Delete chat",
  "session.delete.confirm": "Delete chat \"{{name}}\"?",
  "session.delete.button": "Delete chat",
}

const coworkChatLabelsEt: Partial<Record<keyof Dictionary, string>> = {
  "app.name.desktop": "Andmera",
  "settings.section.desktop": "Rakendus",
  "settings.section.server": "Agendi käituskeskkond",
  "settings.general.row.language.description": "Muuda Andmera kasutajaliidese keelt",
  "settings.general.row.appearance.description": "Kohanda Andmera välimust oma seadmes",
  "settings.general.row.colorScheme.description":
    "Vali, kas Andmera järgib süsteemi kujundust või kasutab heledat või tumedat kujundust",
  "settings.general.row.theme.description": "Kohanda Andmera kujundust.",
  "settings.desktop.wsl.description": "Käivita agendi käituskeskkond Windowsis WSL-i sees.",
  "dialog.server.description": "Vali, millise Andmera käituskeskkonnaga see rakendus ühenduse loob.",
  "dialog.provider.opencode.note": "Hoolikalt valitud mudelid, sealhulgas Claude, GPT, Gemini ja teised",
  "dialog.provider.opencode.tagline": "Töökindlad optimeeritud mudelid",
  "dialog.provider.opencodeGo.tagline": "Soodne tellimus kõigile",
  "dialog.model.unpaid.freeModels.title": "Andmera pakutavad tasuta mudelid",
  "dialog.plugins.empty": "Andmera käituskeskkonna konfiguratsioonis seadistatud pluginad",
  "settings.updates.row.startup.description": "Kontrolli Andmera käivitamisel automaatselt värskendusi",
  "settings.updates.toast.latest.description": "Kasutad Andmera uusimat versiooni.",
  "sidebar.gettingStarted.line1": "Andmeras on mudelid juba seadistatud, nii et saad kohe alustada.",
  "help.tabs.introduction": "Andmera põhineb vahekaartidel.",
  "help.tabs.worktrees":
    "Uus kujundus ei toeta veel Giti tööpuid, kuid see võimalus lisatakse peagi. Kui eelistad jätkata eelmise paigutusega, saad paigutust seadetes vahetada. Arvesta siiski, et mõne nädala pärast jääb kasutusele ainult uus paigutus.",
  "toast.update.description": "Andmera uus versioon ({{version}}) on nüüd installimiseks saadaval.",
  "error.page.report.prefix": "Palun teata sellest veast Andmera meeskonnale",
  "error.chain.checkConfig": "Kontrolli konfiguratsioonis teenusepakkuja ja mudeli nimesid",
  "error.chain.mcpFailed": "MCP serveri „{{name}}” töö nurjus. Andmera ei toeta veel MCP autentimist.",
  "command.category.session": "Vestlus",
  "command.session.previous": "Eelmine vestlus",
  "command.session.next": "Järgmine vestlus",
  "command.session.previous.unseen": "Eelmine lugemata vestlus",
  "command.session.next.unseen": "Järgmine lugemata vestlus",
  "command.session.archive": "Arhiveeri vestlus",
  "command.session.new": "Uus vestlus",
  "ui.promptInput.attachments": "Laadi failid üles",
  "command.session.compact": "Tihenda vestlus",
  "command.session.compact.description": "Tee vestlusest kokkuvõte, et vähendada konteksti mahtu",
  "command.session.fork": "Loo sõnumist haru",
  "command.session.fork.description": "Loo varasema sõnumi põhjal uus vestlus",
  "command.session.share": "Jaga vestlust",
  "command.session.share.description": "Jaga seda vestlust ja kopeeri URL lõikelauale",
  "command.session.unshare": "Lõpeta vestluse jagamine",
  "command.session.unshare.description": "Lõpeta selle vestluse jagamine",
  "command.session.export": "Ekspordi vestlus",
  "command.session.export.description": "Ekspordi kogu vestluse ajalugu JSON-vormingus",
  "palette.search.placeholder": "Otsi faile, käske ja vestlusi",
  "palette.search.placeholder.home": "Otsi käske ja vestlusi",
  "context.export.session": "Ekspordi vestlus",
  "context.stats.session": "Vestlus",
  "context.stats.sessionCreated": "Vestlus loodud",
  "prompt.toast.sessionCreateFailed.title": "Vestluse loomine nurjus",
  "prompt.toast.promptSendFailed.description": "Vestlust ei õnnestunud hankida",
  "help.tabs.toast.ariaLabel": "Tutvustame vahekaarte. Korralda oma tööd ja aktiivseid vestlusi vahekaartide abil",
  "help.tabs.description": "Korralda oma tööd ja aktiivseid vestlusi vahekaartide abil",
  "help.tabs.sessions":
    "Alusta vahekaardil uut vestlust või ava mõne oma projekti olemasolev vestlus. Uut ülesannet alustades ava uus vahekaart ja töö lõpetamisel sulge see.",
  "help.tabs.persistence":
    "Mõne vahekaardi avatuna hoidmine aitab aktiivseid vestlusi korrastada. Kui kavatsed vahekaarte pikemalt avatuna hoida, anna neile meeldejäävad nimed.",
  "help.tabs.home": "Kõik vestlused ja projektid leiad uuelt avalehelt. Vestluse valimisel avaneb see vahekaardil.",
  "toast.session.share.copyFailed.title": "URL-i kopeerimine lõikelauale nurjus",
  "toast.session.share.success.title": "Vestlus on jagatud",
  "toast.session.share.success.description": "Vestluse URL kopeeriti lõikelauale!",
  "toast.session.share.failed.title": "Vestluse jagamine nurjus",
  "toast.session.share.failed.description": "Vestluse jagamisel ilmnes viga",
  "toast.session.unshare.success.title": "Vestluse jagamine on lõpetatud",
  "toast.session.unshare.success.description": "Vestluse jagamine lõpetati edukalt!",
  "toast.session.unshare.failed.title": "Vestluse jagamise lõpetamine nurjus",
  "toast.session.unshare.failed.description": "Vestluse jagamise lõpetamisel ilmnes viga",
  "toast.session.export.success.title": "Vestlus eksporditud",
  "toast.session.export.failed.title": "Vestluse eksportimine nurjus",
  "toast.session.export.failed.description": "Vestluse eksportimisel ilmnes viga",
  "toast.session.listFailed.title": "Projekti {{project}} vestluste laadimine nurjus",
  "notification.action.goToSession": "Mine vestlusse",
  "notification.session.error.title": "Vestluse viga",
  "home.sessions.search.placeholder": "Otsi vestlusi",
  "home.sessions.search.placeholder.scoped": "Otsi vestlusi asukohast {{scope}}",
  "home.sessions.search.sessions": "Vestlused",
  "home.sessions.search.noResults": "Päringule {{query}} ei leitud ühtegi vestlust",
  "home.sessions.empty.description": "Alustamiseks loo vestlus",
  "session.tab.session": "Vestlus",
  "session.tab.unknown": "Tundmatu vestlus",
  "session.tab.review": "Töövoog",
  "session.panel.reviewAndFiles": "Töövoog",
  "session.error.notFound": "Seda vestlust ei leitud",
  "session.error.notFound.description": "See vahekaart viitab vestlusele, mida selles serveris enam pole.",
  "session.review.empty": "Selles vestluses pole veel muudatusi",
  "session.delete.failed.title": "Vestluse kustutamine nurjus",
  "session.delete.title": "Kustuta vestlus",
  "session.delete.confirm": "Kas kustutada vestlus „{{name}}”?",
  "session.delete.button": "Kustuta vestlus",
}

const coworkChatLabelsRu: Partial<Record<keyof Dictionary, string>> = {
  "app.name.desktop": "Andmera",
  "settings.section.desktop": "Приложение",
  "settings.section.server": "Среда выполнения агента",
  "settings.general.row.language.description": "Изменить язык интерфейса Andmera",
  "settings.general.row.appearance.description": "Настроить внешний вид Andmera на этом устройстве",
  "settings.general.row.colorScheme.description": "Выберите, будет ли Andmera использовать системную, светлую или тёмную тему",
  "settings.general.row.theme.description": "Настройте тему Andmera.",
  "settings.desktop.wsl.description": "Запускать среду выполнения агента внутри WSL в Windows.",
  "dialog.server.description": "Выберите среду выполнения Andmera, к которой подключается приложение.",
  "dialog.model.unpaid.freeModels.title": "Бесплатные модели, предоставляемые Andmera",
  "dialog.plugins.empty": "Плагины настроены в конфигурации среды выполнения Andmera",
  "settings.updates.row.startup.description": "Автоматически проверять обновления при запуске Andmera",
  "settings.updates.toast.latest.description": "У вас установлена последняя версия Andmera.",
  "sidebar.gettingStarted.line1": "В Andmera уже настроены модели, поэтому можно начинать сразу.",
  "help.tabs.introduction": "Работа в Andmera построена вокруг вкладок.",
  "toast.update.description": "Доступна новая версия Andmera ({{version}}).",
  "error.page.report.prefix": "Пожалуйста, сообщите об этой ошибке команде Andmera",
  "error.chain.mcpFailed": 'Сервер MCP "{{name}}" завершился с ошибкой. Andmera пока не поддерживает аутентификацию MCP.',
  "command.category.session": "Чат",
  "command.session.previous": "Предыдущий чат",
  "command.session.next": "Следующий чат",
  "command.session.previous.unseen": "Предыдущий непрочитанный чат",
  "command.session.next.unseen": "Следующий непрочитанный чат",
  "command.session.archive": "Архивировать чат",
  "command.session.new": "Новый чат",
  "ui.promptInput.attachments": "Загрузить файлы",
  "command.session.compact": "Сжать чат",
  "command.session.compact.description": "Суммировать чат, чтобы уменьшить размер контекста",
  "command.session.fork": "Создать чат из сообщения",
  "command.session.fork.description": "Создать новый чат из предыдущего сообщения",
  "command.session.share": "Поделиться чатом",
  "command.session.share.description": "Поделиться этим чатом и скопировать URL в буфер обмена",
  "command.session.unshare": "Отключить общий доступ к чату",
  "command.session.unshare.description": "Прекратить общий доступ к этому чату",
  "command.session.export": "Экспортировать чат",
  "command.session.export.description": "Экспортировать полную историю переписки в чате в формате JSON",
  "palette.search.placeholder": "Поиск файлов, команд и чатов",
  "palette.search.placeholder.home": "Поиск команд и чатов",
  "context.export.session": "Экспортировать чат",
  "context.stats.session": "Чат",
  "context.stats.sessionCreated": "Чат создан",
  "prompt.toast.sessionCreateFailed.title": "Не удалось создать чат",
  "prompt.toast.promptSendFailed.description": "Не удалось получить чат",
  "help.tabs.toast.ariaLabel": "Вкладки: организуйте работу и активные чаты",
  "help.tabs.description": "Организуйте работу и активные чаты с помощью вкладок",
  "help.tabs.sessions": "Начните новый чат во вкладке или откройте существующий чат из любого проекта. Открывайте новую вкладку для новой задачи и закрывайте её после завершения.",
  "help.tabs.persistence": "Несколько открытых вкладок помогают организовать активные чаты. Переименуйте вкладки, если планируете оставить их открытыми.",
  "help.tabs.home": "Все чаты и проекты находятся на главном экране. Выбор чата открывает его во вкладке.",
  "toast.session.share.copyFailed.title": "Не удалось скопировать URL в буфер обмена",
  "toast.session.share.success.title": "Доступ к чату открыт",
  "toast.session.share.success.description": "Ссылка на чат скопирована в буфер обмена!",
  "toast.session.share.failed.title": "Не удалось поделиться чатом",
  "toast.session.share.failed.description": "Произошла ошибка при открытии доступа к чату",
  "toast.session.unshare.success.title": "Общий доступ к чату отключён",
  "toast.session.unshare.success.description": "Общий доступ к чату успешно отключён!",
  "toast.session.unshare.failed.title": "Не удалось отключить общий доступ к чату",
  "toast.session.unshare.failed.description": "Произошла ошибка при отключении общего доступа к чату",
  "toast.session.export.success.title": "Чат экспортирован",
  "toast.session.export.failed.title": "Не удалось экспортировать чат",
  "toast.session.export.failed.description": "Произошла ошибка при экспорте чата",
  "toast.session.listFailed.title": "Не удалось загрузить чаты для {{project}}",
  "notification.action.goToSession": "Перейти к чату",
  "notification.session.error.title": "Ошибка чата",
  "home.sessions.search.placeholder": "Поиск чатов",
  "home.sessions.search.placeholder.scoped": "Поиск чатов в {{scope}}",
  "home.sessions.search.sessions": "Чаты",
  "home.sessions.search.noResults": "Чаты по запросу {{query}} не найдены",
  "home.sessions.empty": "Здесь пока ничего нет",
  "home.sessions.empty.description": "Создайте чат, чтобы начать",
  "session.tab.session": "Чат",
  "session.tab.unknown": "Неизвестный чат",
  "session.tab.review": "Рабочий процесс",
  "session.panel.reviewAndFiles": "Рабочий процесс",
  "session.error.notFound": "Этот чат не найден",
  "session.error.notFound.description": "Эта вкладка указывает на чат, которого больше нет на сервере.",
  "session.review.empty": "В этом чате пока нет изменений",
  "session.delete.failed.title": "Не удалось удалить чат",
  "session.delete.title": "Удалить чат",
  "session.delete.confirm": "Удалить чат \"{{name}}\"?",
  "session.delete.button": "Удалить чат",
}

function coworkAgentMode() {
  return import.meta.env.VITE_AGENT_MODE === "true"
}

function coworkTemplate(locale: Locale, key: keyof Dictionary, params?: Record<string, string | number | boolean>) {
  const labels = locale === "et" ? coworkChatLabelsEt : locale === "ru" ? coworkChatLabelsRu : coworkChatLabels
  const label = labels[key]
  if (!label) return
  return i18n.resolveTemplate(label, params)
}

const merge = (app: Promise<Source>, ui: Promise<Source>) =>
  Promise.all([app, ui]).then(([a, b]) => ({ ...base, ...i18n.flatten({ ...a.dict, ...b.dict }) }) as Dictionary)

const loaders: Record<Exclude<Locale, "en">, () => Promise<Dictionary>> = {
  zh: () => merge(import("@/i18n/zh"), import("@opencode-ai/ui/i18n/zh")),
  zht: () => merge(import("@/i18n/zht"), import("@opencode-ai/ui/i18n/zht")),
  ko: () => merge(import("@/i18n/ko"), import("@opencode-ai/ui/i18n/ko")),
  de: () => merge(import("@/i18n/de"), import("@opencode-ai/ui/i18n/de")),
  es: () => merge(import("@/i18n/es"), import("@opencode-ai/ui/i18n/es")),
  fr: () => merge(import("@/i18n/fr"), import("@opencode-ai/ui/i18n/fr")),
  da: () => merge(import("@/i18n/da"), import("@opencode-ai/ui/i18n/da")),
  ja: () => merge(import("@/i18n/ja"), import("@opencode-ai/ui/i18n/ja")),
  pl: () => merge(import("@/i18n/pl"), import("@opencode-ai/ui/i18n/pl")),
  ru: () => merge(import("@/i18n/ru"), import("@opencode-ai/ui/i18n/ru")),
  uk: () => merge(import("@/i18n/uk"), import("@opencode-ai/ui/i18n/uk")),
  ar: () => merge(import("@/i18n/ar"), import("@opencode-ai/ui/i18n/ar")),
  no: () => merge(import("@/i18n/no"), import("@opencode-ai/ui/i18n/no")),
  br: () => merge(import("@/i18n/br"), import("@opencode-ai/ui/i18n/br")),
  th: () => merge(import("@/i18n/th"), import("@opencode-ai/ui/i18n/th")),
  bs: () => merge(import("@/i18n/bs"), import("@opencode-ai/ui/i18n/bs")),
  tr: () => merge(import("@/i18n/tr"), import("@opencode-ai/ui/i18n/tr")),
  hi: () => merge(import("@/i18n/hi"), import("@opencode-ai/ui/i18n/hi")),
  nl: () => merge(import("@/i18n/nl"), import("@opencode-ai/ui/i18n/nl")),
  id: () => merge(import("@/i18n/id"), import("@opencode-ai/ui/i18n/id")),
  vi: () => merge(import("@/i18n/vi"), import("@opencode-ai/ui/i18n/vi")),
  it: () => merge(import("@/i18n/it"), import("@opencode-ai/ui/i18n/it")),
  ur: () => merge(import("@/i18n/ur"), import("@opencode-ai/ui/i18n/ur")),
  pa: () => merge(import("@/i18n/pa"), import("@opencode-ai/ui/i18n/pa")),
  az: () => merge(import("@/i18n/az"), import("@opencode-ai/ui/i18n/az")),
  fi: () => merge(import("@/i18n/fi"), import("@opencode-ai/ui/i18n/fi")),
  sv: () => merge(import("@/i18n/sv"), import("@opencode-ai/ui/i18n/sv")),
  am: () => merge(import("@/i18n/am"), import("@opencode-ai/ui/i18n/am")),
  bg: () => merge(import("@/i18n/bg"), import("@opencode-ai/ui/i18n/bg")),
  bn: () => merge(import("@/i18n/bn"), import("@opencode-ai/ui/i18n/bn")),
  ca: () => merge(import("@/i18n/ca"), import("@opencode-ai/ui/i18n/ca")),
  cs: () => merge(import("@/i18n/cs"), import("@opencode-ai/ui/i18n/cs")),
  dv: () => merge(import("@/i18n/dv"), import("@opencode-ai/ui/i18n/dv")),
  dz: () => merge(import("@/i18n/dz"), import("@opencode-ai/ui/i18n/dz")),
  el: () => merge(import("@/i18n/el"), import("@opencode-ai/ui/i18n/el")),
  et: () => merge(import("@/i18n/et"), import("@opencode-ai/ui/i18n/et")),
  fa: () => merge(import("@/i18n/fa"), import("@opencode-ai/ui/i18n/fa")),
  fo: () => merge(import("@/i18n/fo"), import("@opencode-ai/ui/i18n/fo")),
  hr: () => merge(import("@/i18n/hr"), import("@opencode-ai/ui/i18n/hr")),
  hu: () => merge(import("@/i18n/hu"), import("@opencode-ai/ui/i18n/hu")),
  hy: () => merge(import("@/i18n/hy"), import("@opencode-ai/ui/i18n/hy")),
  is: () => merge(import("@/i18n/is"), import("@opencode-ai/ui/i18n/is")),
  ka: () => merge(import("@/i18n/ka"), import("@opencode-ai/ui/i18n/ka")),
  km: () => merge(import("@/i18n/km"), import("@opencode-ai/ui/i18n/km")),
  lo: () => merge(import("@/i18n/lo"), import("@opencode-ai/ui/i18n/lo")),
  lt: () => merge(import("@/i18n/lt"), import("@opencode-ai/ui/i18n/lt")),
  lv: () => merge(import("@/i18n/lv"), import("@opencode-ai/ui/i18n/lv")),
  mk: () => merge(import("@/i18n/mk"), import("@opencode-ai/ui/i18n/mk")),
  mn: () => merge(import("@/i18n/mn"), import("@opencode-ai/ui/i18n/mn")),
  ms: () => merge(import("@/i18n/ms"), import("@opencode-ai/ui/i18n/ms")),
  my: () => merge(import("@/i18n/my"), import("@opencode-ai/ui/i18n/my")),
  ne: () => merge(import("@/i18n/ne"), import("@opencode-ai/ui/i18n/ne")),
  ro: () => merge(import("@/i18n/ro"), import("@opencode-ai/ui/i18n/ro")),
  si: () => merge(import("@/i18n/si"), import("@opencode-ai/ui/i18n/si")),
  sk: () => merge(import("@/i18n/sk"), import("@opencode-ai/ui/i18n/sk")),
  sl: () => merge(import("@/i18n/sl"), import("@opencode-ai/ui/i18n/sl")),
  sq: () => merge(import("@/i18n/sq"), import("@opencode-ai/ui/i18n/sq")),
  sr: () => merge(import("@/i18n/sr"), import("@opencode-ai/ui/i18n/sr")),
  tg: () => merge(import("@/i18n/tg"), import("@opencode-ai/ui/i18n/tg")),
  tk: () => merge(import("@/i18n/tk"), import("@opencode-ai/ui/i18n/tk")),
  uz: () => merge(import("@/i18n/uz"), import("@opencode-ai/ui/i18n/uz")),
}

function loadDict(locale: Locale) {
  const hit = dicts.get(locale)
  if (hit) return Promise.resolve(hit)
  if (locale === "en") return Promise.resolve(base)
  const load = loaders[locale]
  return load().then((next: Dictionary) => {
    dicts.set(locale, next)
    return next
  })
}

export function loadLocaleDict(locale: Locale) {
  return loadDict(locale).then(() => undefined)
}

function detectLocale(): Locale {
  if (typeof navigator !== "object") return "en"
  return detectDesktopNativeLocale(navigator.languages?.length ? navigator.languages : [navigator.language])
}

export function normalizeLocale(value: string): Locale {
  return LOCALES.includes(value as Locale) ? (value as Locale) : "en"
}

function readStoredLocale() {
  if (typeof localStorage !== "object") return
  try {
    const raw = localStorage.getItem("opencode.global.dat:language")
    if (!raw) return
    const next = JSON.parse(raw) as { locale?: string }
    if (typeof next?.locale !== "string") return
    return normalizeLocale(next.locale)
  } catch {
    return
  }
}

const warm = readStoredLocale() ?? detectLocale()
const initialLocale =
  warm === "en"
    ? Promise.resolve(warm)
    : loadDict(warm).then(
        () => warm,
        () => "en" as const,
      )

export function loadInitialLocale() {
  return initialLocale
}

export const { use: useLanguage, provider: LanguageProvider } = createSimpleContext({
  name: "Language",
  gate: false,
  init: (props: { locale?: Locale; onNativeTranslations?: (bundle: DesktopNativeBundle) => void }) => {
    const initial = props.locale ?? readStoredLocale() ?? detectLocale()
    const [store, setStore, _, ready] = persisted(
      Persist.global("language", ["language.v1"]),
      createStore({
        locale: initial,
      }),
    )

    const locale = createMemo<Locale>(() => normalizeLocale(store.locale))
    const intl = createMemo(() => INTL[locale()])
    const [layout, setLayout] = createStore({ direction: undefined as Direction | undefined })
    const direction = createMemo(() => layout.direction ?? localeDirection(locale()))
    const layoutLocale = createMemo(() => {
      if (!layout.direction) return intl()
      // Kobalte derives menu direction from locale rather than accepting a direction override.
      return layout.direction === "rtl" ? "ar" : "en"
    })

    const [dict] = createResource(locale, loadDict, {
      initialValue: dicts.get(initial) ?? base,
    })

    const translate = i18n.translator(() => dict() ?? base, i18n.resolveTemplate) as (
      key: keyof Dictionary,
      params?: Record<string, string | number | boolean>,
    ) => string

    const t = (key: keyof Dictionary, params?: Record<string, string | number | boolean>) => {
      if (coworkAgentMode()) return coworkTemplate(locale(), key, params) ?? translate(key, params)
      return translate(key, params)
    }

    const plural = (key: PluralKey, count: number, params?: Record<string, string | number | boolean>) => {
      const category = pluralCategory(intl(), count)
      const current = (dict.loading ? base : (dict() ?? base)) as Record<string, string>
      const candidate = `${key}.${category}`
      const fallback = `${key}.other`
      return i18n.resolveTemplate(current[candidate] ?? current[fallback] ?? fallback, { ...params, count })
    }

    const label = (value: Locale) => DESKTOP_NATIVE_LABELS[value]
    const formatDateTime = (value: Date | number) =>
      dateFormatter(intl(), {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        fractionalSecondDigits: 3,
      }).format(value)

    createEffect(() => {
      if (typeof document !== "object") return
      const value = locale()
      document.documentElement.lang = intl()
      document.documentElement.dir = direction()
      document.cookie = cookie(value)
    })

    createEffect(() => {
      if (!props.onNativeTranslations || dict.loading) return
      const current = dict()
      if (!current) return
      props.onNativeTranslations(
        createDesktopNativeBundle(locale(), (key) => current[key] ?? DESKTOP_NATIVE_ENGLISH[key]),
      )
    })

    return {
      ready,
      locale,
      intl,
      direction,
      layoutLocale,
      locales: LOCALES,
      label,
      formatDateTime,
      t,
      plural,
      setLocale(next: Locale) {
        setStore("locale", normalizeLocale(next))
      },
      setDirection(next: Direction) {
        setLayout("direction", next === localeDirection(locale()) ? undefined : next)
      },
    }
  },
})
